import { useMemo, useState, type FormEvent } from 'react';
import {
  Bouton, Carte, Dialogue, Message, Selecteur, Vide, ZoneTexte,
  EnTetePage, couleurDonnees, useRetours, useTheme,
  ActionsDialogue, IconeTelecharger, SqueletteTableau,
} from '@journal/ui';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import {
  SelecteurContenu, sansPrefixeNiveau, type ElementInserable,
} from '../components/SelecteurContenu.tsx';
import { blocsDeLaCase, indexerBlocs, TableauProgrammation } from '../components/TableauProgrammation.tsx';
import { lireSemainesPerso, semainesDePeriode } from './Reglages.tsx';
import { useSession } from '../lib/session.tsx';
import { useAnneeActive } from '../lib/annee.tsx';
import type { Matiere, Periode } from './Reglages.tsx';
import { useTelechargementPdf } from '../lib/impression.tsx';
import { demandeV1 } from '../lib/impression.ts';
import { programmationV1, titreV1 } from '../lib/gabarits-v1.ts';

/**
 * Programmation : ce que l'on prévoit de traiter, par période et par matière.
 *
 * Un cran au-dessus des progressions, qui sont hebdomadaires. La programmation
 * cadre la période entière ; la progression détaille la semaine ; le cahier
 * journal consigne la séance. C'est cet emboîtement que la vue jour du cahier
 * journal remet côte à côte.
 *
 * ── Blocs libres et blocs issus du programme ────────────────────────────────
 * Un bloc peut être saisi librement, ou provenir du référentiel officiel. Les
 * seconds portent `source: 'programme'` et un identifiant d'objectif : on ne
 * doit pas pouvoir en réécrire le texte, sinon la référence au programme ne
 * signifie plus rien.
 */

export interface BlocProgrammation {
  id: string;
  subjectId: string;
  subjectAreaId: string | null;
  /** DOMAINE du référentiel : la ligne du tableau. */
  curriculumDomainId: string | null;
  /** Sous-domaine de l'objectif, quand le bloc vient du programme. */
  curriculumSubdomainId: string | null;
  termId: string;
  content: string;
  position: number;
  source: 'libre' | 'programme';
  curriculumObjectiveId: string | null;
}

/**
 * Le contenu d'un bloc, ramené à ce qu'une boîte de confirmation peut citer.
 *
 * Un bloc vide se dit en toutes lettres : une boîte qui demande « retirer
 * ceci ? » en montrant du vide ne laisse rien à reconnaître.
 */
export function extraitLisible(contenu: string, maximum = 160): string {
  const propre = contenu.trim();
  if (!propre) return 'Ce bloc est vide.';
  return propre.length > maximum ? `${propre.slice(0, maximum).trimEnd()}…` : propre;
}

export function Programmation() {
  const { api } = useSession();
  const { notifier, confirmer } = useRetours();
  const annee = useAnneeActive();

  const periodes = useDonnees<Periode[]>('/terms');
  const reglagesEcran = useDonnees<Record<string, string>>('/settings');
  const matieres = useDonnees<Matiere[]>('/subjects');
  const [periodeId] = useState('');

  // Période retenue : celle choisie, sinon la première de l'année. Sans repli,
  // l'écran resterait vide au premier affichage alors que des blocs existent.
  const periodeActive = periodeId || periodes.donnees?.[0]?.id || '';
  const blocs = useDonnees<BlocProgrammation[]>(
    '/plan-blocks',
  );
  const { executer, enCours, erreur: erreurEcriture } = useEcriture();

  const [ouvert, setOuvert] = useState(false);
  const [edite] = useState<BlocProgrammation | null>(null);
  const [matiereId, setMatiereId] = useState('');
  const [contenu, setContenu] = useState('');

  const liste = blocs.donnees ?? [];

  /**
   * Insertion depuis le référentiel — reprend `addBlocFromProgramme`.
   *
   * Programmer une période consiste à répartir les objectifs du programme entre
   * les cinq périodes. Les retaper à la main est exactement le travail que
   * l'application doit éviter, et c'est aussi ainsi qu'on s'écarte de
   * l'intitulé officiel sans s'en rendre compte.
   *
   * Le bloc inséré porte `source: 'programme'` et l'identifiant de l'objectif :
   * l'écran s'en sert pour interdire sa réécriture, afin que la référence au
   * référentiel ne devienne pas mensongère.
   */
  const [selecteurMatiereId, setSelecteurMatiereId] = useState<string | null>(null);
  /** Ligne visée par le bouton « Programme » : son nom et tous ses identifiants. */
  const [ligneCible, setLigneCible] = useState<
    { id: string | null; nom: string; ids: string[] } | null
  >(null);
  const [periodeCible, setPeriodeCible] = useState('');
  // Le programme de la CLASSE : une classe à cheval sur deux cycles les reçoit
  // tous les deux, là où une version unique en effaçait un.
  const arbre = useDonnees<Array<{
    id: string;
    name: string;
    subject: string;
    color: string;
    subdomains: Array<{
      id: string; name: string; objectives: Array<{ id: string; text: string }>;
    }>;
  }>>('/curriculum/tree');

  /*
   * Matière du tableau — et rien d'autre.
   *
   * Le référentiel officiel a trois niveaux : matière → domaine → sous-domaine.
   * Pour « Français », les domaines sont « Lecture », « Écriture », « Oral »…
   * et leurs sous-domaines sont les notions détaillées (« CM1 — Lire avec
   * fluidité »).
   *
   * L'enseignant, lui, appelle « sous-domaines » ces domaines-là : ce sont eux
   * qu'il attend en lignes. L'application d'origine ne s'y trompe pas — elle
   * rapproche le sous-domaine de l'enseignant du champ `nom` du DOMAINE
   * officiel. Cet écran demandait donc de choisir un domaine, puis affichait
   * ses notions en lignes : un cran trop bas, et un menu de trop.
   */
  const [matiereTableau, setMatiereTableau] = useState('');

  const matieresProgramme = useMemo(
    () => [...new Set((arbre.donnees ?? []).map((d) => d.subject).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'fr')),
    [arbre.donnees],
  );
  const matiereActive = matiereTableau || matieresProgramme[0] || '';

  const domainesDeLaMatiere = useMemo(
    () => (arbre.donnees ?? []).filter((d) => d.subject === matiereActive),
    [arbre.donnees, matiereActive],
  );
  /**
   * Lignes du tableau : les domaines de la matière, REGROUPÉS PAR NOM.
   *
   * Le référentiel répète un même domaine une fois par niveau : une classe de
   * CM1-CM2 porte deux « Lecture », d'identifiants différents. Les afficher
   * tels quels donnerait deux lignes identiques que l'enseignant remplirait au
   * hasard. On regroupe donc par nom, et la ligne retient TOUS les identifiants
   * du groupe : elle montre ce qui a été saisi sous l'un comme sous l'autre.
   */
  const lignesTableau = useMemo(() => {
    const parNom = new Map<string, string[]>();
    for (const d of domainesDeLaMatiere) {
      const nom = sansPrefixeNiveau(d.name);
      parNom.set(nom, [...(parNom.get(nom) ?? []), d.id]);
    }
    return [
      ...[...parNom].map(([nom, ids]) => ({ id: ids[0] as string | null, nom, ids })),
      // Toujours présente : elle recueille les blocs libres et ceux dont le
      // domaine a disparu d'une réforme du programme.
      { id: null, nom: 'Général', ids: [] as string[] },
    ];
  }, [domainesDeLaMatiere]);

  const idMatiereClient = matieres.donnees?.find((m) => m.name === matiereActive)?.id ?? '';
  const pdf = useTelechargementPdf();

  /*
   * Repli : la teinte d'ardoise de la palette de données. Un violet écrit en
   * dur figurait ici et dans les progressions — même valeur, deux endroits, et
   * il n'appartenait à aucune palette.
   */
  const { theme } = useTheme();
  const couleurMatiere = matieres.donnees?.find((m) => m.name === matiereActive)?.color
    ?? domainesDeLaMatiere[0]?.color ?? couleurDonnees(7, theme);

  const blocsDuTableau = useMemo(
    () => (blocs.donnees ?? []).filter((b) => b.subjectId === idMatiereClient),
    [blocs.donnees, idMatiereClient],
  );

  const colonnes = useMemo(
    () => (periodes.donnees ?? []).map((p) => ({
      id: p.id,
      nom: p.name,
      numero: p.number,
      debut: p.startsOn,
      fin: p.endsOn,
      semaines: semainesDePeriode(p, lireSemainesPerso(reglagesEcran.donnees?.semaines_perso)).length,
    })),
    [periodes.donnees, reglagesEcran.donnees],
  );

  /*
   * Le PDF de l'année, au modèle de la v1 (`lib/gabarits-v1.ts`) : périodes
   * à la couleur de la matière, sous-domaines en bande, blocs teintés.
   */
  const impression = {
    enCours: pdf.enCours,
    erreur: pdf.erreur,
    lancer: () => {
      const index = indexerBlocs(blocsDuTableau.map((b) => ({
        id: b.id, curriculumDomainId: b.curriculumDomainId, termId: b.termId,
        content: b.content, source: b.source,
      })));
      void pdf.telecharger(demandeV1(programmationV1(
        titreV1(matiereActive, annee.donnees?.label),
        couleurMatiere,
        colonnes,
        lignesTableau.map((l) => ({
          nom: l.nom,
          blocs: (periodeId: string) => blocsDeLaCase(index, l, periodeId).map((b) => b.content),
        })),
      )));
    },
  };

  /** Crée un bloc vide dans une case, prêt à recevoir la frappe. */
  async function ajouterBloc(domaineId: string | null, periodeIdCible: string) {
    /*
     * Le NOM de la matière accompagne toujours l'identifiant.
     *
     * Une école neuve n'a aucune ligne `subjects` : l'identifiant est vide, et
     * la fonction sortait ici sans rien faire ni rien dire — le bouton
     * « Libre » paraissait cassé. Le serveur matérialise la matière à partir de
     * son nom de programme, qui est de toute façon la source.
     */
    const ok = await executer(() =>
      api.requete('/plan-blocks', {
        methode: 'POST',
        corps: {
          ...(idMatiereClient ? { subjectId: idMatiereClient } : {}),
          subjectName: matiereActive,
          curriculumDomainId: domaineId,
          termId: periodeIdCible,
          content: '',
          source: 'libre',
          position: blocsDuTableau.length,
        },
      }));
    /*
     * Les matières sont rechargées AVEC les blocs.
     *
     * Le serveur a pu créer la ligne `subjects` à l'instant. Sans ce
     * rechargement, `idMatiereClient` reste vide côté client et le filtre du
     * tableau — qui compare `subjectId` — masquerait le bloc qu'on vient de
     * créer : il existerait sans se voir.
     */
    if (ok) await Promise.all([blocs.recharger(), matieres.recharger()]);
  }

  async function modifierBloc(id: string, contenu: string): Promise<boolean> {
    try {
      await api.requete(`/plan-blocks/${id}`, { methode: 'PATCH', corps: { content: contenu } });
      await blocs.recharger();
      return true;
    } catch { return false; }
  }

  /**
   * Contenu proposé par le bouton « Programme ».
   *
   * Limité au DOMAINE de la ligne cliquée — ligne « Lecture » : uniquement le
   * programme de Lecture. C'est ce que fait l'application d'origine, qui filtre
   * son contenu par le nom du domaine de la ligne. Sans ce filtre, une ligne
   * proposait le programme entier de la matière et l'on rangeait, sans le
   * vouloir, de l'orthographe dans « Lecture ».
   */
  const contenuProgramme = useMemo<ElementInserable[]>(() => {
    if (!ligneCible) return [];
    const retenus = new Set(ligneCible.ids);
    const liste: ElementInserable[] = [];
    for (const domaine of arbre.donnees ?? []) {
      if (!retenus.has(domaine.id)) continue;
      for (const sd of domaine.subdomains) {
        const notion = sansPrefixeNiveau(sd.name);
        for (const o of sd.objectives) {
          liste.push({
            cle: o.id,
            chemin: `${sansPrefixeNiveau(domaine.name)} · ${notion}`,
            texte: o.text,
            sousDomaineId: sd.id,
          });
        }
      }
    }
    return liste;
  }, [ligneCible, arbre.donnees]);

  async function insererDepuisProgramme(element: ElementInserable) {
    if (!ligneCible || !periodeCible) return;
    const ok = await executer(() =>
      api.requete('/plan-blocks', {
        methode: 'POST',
        corps: {
          ...(selecteurMatiereId ? { subjectId: selecteurMatiereId } : {}),
          subjectName: matiereActive,
          // La case d'où vient le clic : le bloc doit atterrir là où on l'a
          // demandé, pas dans une période par défaut.
          curriculumDomainId: ligneCible.id,
          curriculumSubdomainId: element.sousDomaineId ?? null,
          termId: periodeCible,
          content: element.texte,
          source: 'programme',
          curriculumObjectiveId: element.cle,
          position: (blocs.donnees ?? []).length,
        },
      }));
    if (ok) {
      // Une ligne choisie, la fenêtre se ferme : on revient au tableau voir
      // le bloc posé. Pour en ajouter une autre, on rouvre « Programme ».
      setSelecteurMatiereId(null);
      notifier('Bloc ajouté depuis le programme.');
      // Matières rechargées aussi : le serveur a pu créer la ligne à l'instant.
      await Promise.all([blocs.recharger(), matieres.recharger()]);
    }
  }

  async function enregistrer(evenement: FormEvent) {
    evenement.preventDefault();
    const corps = {
      subjectId: matiereId,
      termId: periodeActive,
      content: contenu.trim(),
      source: 'libre' as const,
    };
    const ok = await executer(() =>
      edite
        ? api.requete(`/plan-blocks/${edite.id}`, {
            methode: 'PATCH',
            // La matière et la période ne changent pas à l'édition : déplacer
            // un bloc revient à le recréer, geste plus lisible qu'un
            // changement silencieux de colonne.
            corps: { content: corps.content },
          })
        : api.requete('/plan-blocks', { methode: 'POST', corps }));
    if (ok) {
      setOuvert(false);
      await blocs.recharger();
    }
  }

  async function supprimer(bloc: BlocProgrammation) {
    const ok = await confirmer({
      titre: 'Retirer ce bloc ?',
      /*
       * Une coupe FRANCHE, marquée par des points de suspension.
       *
       * Le contenu partait tel quel, tronqué à cent soixante caractères en
       * plein milieu d'un mot : on ne savait pas s'il restait du texte
       * au-delà, ni si le bloc s'arrêtait vraiment là.
       */
      message: extraitLisible(bloc.content),
      libelleConfirmation: 'Retirer',
      destructeur: true,
    });
    if (!ok) return;
    if (await executer(() => api.requete(`/plan-blocks/${bloc.id}`, { methode: 'DELETE' }))) {
      notifier('Bloc retiré.');
      await blocs.recharger();
    }
  }

  if (annee.erreur) {
    return (
      <Carte titre="Programmation">
        <Vide titre="Aucune année scolaire active">
          La programmation se répartit sur les périodes de l'année en cours.
          Créez une année scolaire dans les réglages pour commencer.
        </Vide>
      </Carte>
    );
  }

  if (periodes.chargement && !periodes.donnees) {
    return (
      <div className="flex flex-col gap-5">
        {impression.erreur && <Message ton="erreur">{impression.erreur}</Message>}

      <EnTetePage titre="Programmation" />
        <Carte><SqueletteTableau lignes={4} colonnes={2} /></Carte>
      </div>
    );
  }
  if (periodes.erreur) return <Message ton="erreur">{periodes.erreur}</Message>;

  if ((periodes.donnees?.length ?? 0) === 0) {
    return (
      <Carte titre="Programmation">
        <Vide titre="Aucune période définie">
          Les périodes suivent le calendrier officiel et se créent avec l’année
          scolaire. Si elles manquent, prévenez la direction de l’école ou
          écrivez au support.
        </Vide>
      </Carte>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {erreurEcriture && <Message ton="erreur">{erreurEcriture}</Message>}

      <EnTetePage
        titre="Programmation"
        contexte={
          periodes.donnees?.find((p) => p.id === periodeActive)
            ? `${periodes.donnees.find((p) => p.id === periodeActive)!.name}`
              + ` · ${liste.length} bloc${liste.length > 1 ? 's' : ''}`
            : undefined
        }
        actions={
          <Bouton
            variante="ghost"
            icone={<IconeTelecharger taille={16} />}
            chargement={impression.enCours}
            onClick={impression.lancer}
          >
            Télécharger le PDF
          </Bouton>
        }
      />

      <Carte
        titre="Programmation de l'année"
        description="Une colonne par période, une ligne par sous-domaine du programme."
        action={
          <div className="flex flex-wrap items-end gap-2">
            {matieresProgramme.length > 1 && (
              <Selecteur
                libelle="Matière"
                value={matiereActive}
                onChange={(e) => setMatiereTableau(e.target.value)}
                options={matieresProgramme.map((m) => ({ valeur: m, texte: m }))}
              />
            )}
          </div>
        }
      >
        {(periodes.donnees?.length ?? 0) === 0 ? (
          <Vide titre="Aucune période définie">
            Les périodes suivent le calendrier officiel et se créent avec l’année
            scolaire. Si elles manquent, prévenez la direction de l’école ou
            écrivez au support.
          </Vide>
        ) : domainesDeLaMatiere.length === 0 ? (
          <Vide titre="Aucun domaine dans le programme">
            Le référentiel ne propose rien pour vos niveaux. Vérifiez-les dans
            l'onglet Général des réglages.
          </Vide>
        ) : blocs.chargement && !blocs.donnees ? (
          <SqueletteTableau lignes={5} colonnes={5} />
        ) : (
          <>
          {/*
            * Le seul aplat de couleur de l'écran : il dit de quelle matière il
            * s'agit, une fois, en haut. Le tableau lui-même reste neutre — la
            * teinte y serait identique sur toutes les lignes, donc muette.
            */}
          <div aria-hidden="true" style={{ height: 3, background: couleurMatiere }} />

          <TableauProgrammation
            periodes={colonnes}
            sousDomaines={lignesTableau}
            blocs={blocsDuTableau.map((b) => ({
              id: b.id,
              curriculumDomainId: b.curriculumDomainId,
              termId: b.termId,
              content: b.content,
              source: b.source,
            }))}
            surAjoutLibre={(ligne, p) => void ajouterBloc(ligne.id, p)}
            surAjoutProgramme={(ligne, p) => {
              setLigneCible(ligne);
              setPeriodeCible(p);
              setSelecteurMatiereId(idMatiereClient);
            }}
            surModification={(bloc, contenu) => modifierBloc(bloc.id, contenu)}
            surSuppression={(bloc) => void supprimer(
              (blocs.donnees ?? []).find((b) => b.id === bloc.id)!,
            )}
          />
          </>
        )}
      </Carte>

      <Dialogue
        titre={edite ? 'Modifier le bloc' : 'Nouveau bloc de programmation'}
        ouvert={ouvert}
        surFermeture={() => setOuvert(false)}
      >
        <form onSubmit={enregistrer} className="flex flex-col gap-4">
          {!edite && (
            <Selecteur
              libelle="Matière"
              value={matiereId}
              onChange={(e) => setMatiereId(e.target.value)}
              options={(matieres.donnees ?? []).map((m) => ({ valeur: m.id, texte: m.name }))}
            />
          )}
          <ZoneTexte
            libelle="Contenu"
            value={contenu}
            onChange={(e) => setContenu(e.target.value)}
            autoFocus
          />
          <ActionsDialogue>
            <Bouton variante="ghost" onClick={() => setOuvert(false)}>
              Annuler
            </Bouton>
            <Bouton type="submit" chargement={enCours} disabled={!matiereId || !contenu.trim()}>
              Enregistrer
            </Bouton>
          </ActionsDialogue>
        </form>
      </Dialogue>

      <SelecteurContenu
        ouvert={selecteurMatiereId !== null}
        surFermeture={() => setSelecteurMatiereId(null)}
        titre={
          `Depuis le programme · ${
            matieres.donnees?.find((m) => m.id === selecteurMatiereId)?.name ?? 'matière'
          }`
        }
        // Le programme seul, sans onglets : la progression n'a pas cours sur
        // cet écran, qui répartit le programme sur l'année.
        programme={contenuProgramme}
        surInsertion={(element) => void insererDepuisProgramme(element)}
        enCours={enCours}
      />
    </div>
  );
}
